/**
 * Poligon penggolong: digambar di atas awan, disimpan, diterbitkan sebagai kurva.
 *
 * Poligon seleksi yang sudah ada di layar crossplot menjawab "yang ini apa"
 * dan berhenti di situ: ia menghitung titik yang tergambar, memajang
 * ringkasannya, lalu hilang bersama halamannya. Yang tidak bisa dikerjakannya
 * justru satu-satunya hal yang membuat orang menggambarnya — membawa batas itu
 * kembali ke lembar log, sebagai kurva berlabel di sebelah kurva yang
 * melahirkannya. Itulah yang dikerjakan panel ini.
 *
 * Tiga hal yang dijaga di sini, dan ketiganya jenis kekeliruan yang tidak
 * meninggalkan jejak apa pun setelah tergambar:
 *
 * - **Simpulnya disimpan dalam satuan kurva**, bukan pecahan kotak gambar.
 *   Pecahan kotak hanya berarti selama kedua ujung sumbunya tidak berubah, dan
 *   ujung sumbu adalah hal yang diketik ulang orang setiap kali ia memperbesar
 *   sebuah awan. `vertexToValue` yang mengubahnya, dan pembalikan sumbu
 *   densitas ikut dibatalkan di sana.
 * - **Ruang sumbunya ikut tersimpan.** Sebuah bentuk yang digambar di atas
 *   sumbu RT logaritmik adalah bentuk yang lain sama sekali dibaca sebagai
 *   bentuk linier, dan yang menggolongkan di server memakai ruang yang
 *   tersimpan itu — bukan ruang yang kebetulan tampil di layar.
 * - **Yang dipajang hanya poligon yang berdiri di atas sepasang sumbu yang
 *   sedang tampil.** Sebuah poligon NPHI–RHOB yang digambar ulang di atas
 *   GR–RT tetap tergambar rapi sebagai bentuk yang tidak melingkari apa pun
 *   yang dimaksud siapa pun.
 */

import { useMemo, useState } from "react";

import { api, unpage, type Page } from "../api";
import { canCompute } from "../auth";
import { useAsync } from "../hooks";
import { count } from "../lib/plural";
import { SERIES_COLORS, vertexToValue, type AxisMap, type Vertex } from "../lib/xplot";
import type { CrossplotPolygon, CrossplotPublish, Project } from "../types";
import { ColorPicker } from "./ColorPicker";
import { DeleteDialog } from "./DeleteDialog";
import {
  Button,
  Empty,
  KeyValue,
  Notice,
  NumberField,
  Panel,
  TextField,
  Toggle,
  useToast,
} from "./ui";

/** Nama kurva dan set bawaan — sama dengan yang dipakai `publish` di server. */
const DEFAULT_MNEMONIC = "XPLOT_CLASS";
const DEFAULT_SET = "XPLOT";

/**
 * Seluruh poligon sebuah lapangan, dan pegangan untuk memuatnya ulang.
 *
 * Seluruhnya dan bukan yang sepasang sumbu ini saja, sekalipun yang tergambar
 * hanya sebagian: kode kelas bersifat **satu lapangan** — begitu pula namanya —
 * jadi kode bebas berikutnya tidak bisa dihitung dari daftar yang sudah
 * disaring. Sebuah poligon baru yang mengambil kode 2 karena kode itu bebas di
 * antara poligon NPHI–RHOB akan ditolak server karena sudah dipakai sebuah
 * poligon GR–RT yang tidak pernah terlihat di layar ini.
 */
export function useCrossplotPolygons(projectId: number) {
  return useAsync(
    async (signal) =>
      unpage(
        await api.get<Page<CrossplotPolygon>>(
          "/api/crossplot-polygons/",
          { project: projectId },
          signal,
        ),
        signal,
      ),
    [projectId],
  );
}

/** Poligon yang berdiri di atas sepasang sumbu ini, menurut mnemonic kurvanya. */
export function onTheseAxes(
  polygons: CrossplotPolygon[],
  xCurve: string,
  yCurve: string,
): CrossplotPolygon[] {
  const x = xCurve.trim().toUpperCase();
  const y = yCurve.trim().toUpperCase();
  return polygons.filter((polygon) => polygon.x_curve === x && polygon.y_curve === y);
}

/** Kode kelas bebas terkecil, dihitung atas seluruh lapangan. */
function freeClass(polygons: CrossplotPolygon[]): number {
  const used = new Set(polygons.map((polygon) => polygon.class_value));
  let code = 1;
  while (used.has(code)) code += 1;
  return code;
}

/** Warna berikutnya yang belum dipakai poligon mana pun di lapangan ini. */
function freeColor(polygons: CrossplotPolygon[]): string {
  const used = new Set(polygons.map((polygon) => polygon.color.toLowerCase()));
  return (
    SERIES_COLORS.find((color) => !used.has(color.toLowerCase()))
    ?? SERIES_COLORS[polygons.length % SERIES_COLORS.length]
  );
}

export function CrossplotPolygons({
  project,
  polygons,
  reload,
  xCurve,
  yCurve,
  logX,
  logY,
  axes,
  drawn,
  onSaved,
  wellIds,
  show,
  onShowChange,
  onPublished,
  editingId = null,
  onEdit,
  onEditDone,
}: {
  project: Project;
  /** Seluruh poligon lapangan — lihat `useCrossplotPolygons`. */
  polygons: CrossplotPolygon[];
  reload: () => void;
  xCurve: string;
  yCurve: string;
  logX: boolean;
  logY: boolean;
  /**
   * Sumbu yang sedang tergambar, atau null ketika belum ada yang bisa diplot.
   *
   * Tanpa keduanya sebuah bentuk tidak bisa diubah menjadi nilai kurva, dan
   * menyimpannya sebagai pecahan kotak gambar berarti menyimpan angka yang
   * berhenti berarti pada perubahan sumbu berikutnya.
   */
  axes: { x: AxisMap; y: AxisMap } | null;
  /** Bentuk yang baru saja digambar, dalam pecahan kotak gambar. */
  drawn: Vertex[];
  onSaved: () => void;
  /** Sumur dalam lingkup; kosong berarti seluruh sumur lapangan. */
  wellIds: number[];
  show: boolean;
  onShowChange: (show: boolean) => void;
  onPublished?: () => void;
  /** Kelas yang simpulnya sedang disunting di plot, bila ada. */
  editingId?: number | null;
  /** Muat simpul sebuah kelas ke plot untuk disunting. */
  onEdit?: (polygon: CrossplotPolygon) => void;
  /** Penyuntingan selesai — disimpan atau dibatalkan. */
  onEditDone?: () => void;
}) {
  const toast = useToast();
  const mayPublish = canCompute(project.my_role);

  const mine = useMemo(() => onTheseAxes(polygons, xCurve, yCurve), [polygons, xCurve, yCurve]);
  const elsewhere = polygons.length - mine.length;

  /* Yang boleh berangkat bersama dalam satu penerbitan.
     Server menolak campuran — kuncinya sepasang mnemonic **berikut** kedua
     bendera log — sebab satu kurva kelas tidak bisa dibaca di dua ruang
     sekaligus: sisi yang lurus di ruang log10 melengkung di ruang nilai, dan
     tidak ada apa pun di dalam kurvanya yang bisa mengatakan sampel mana milik
     ruang yang mana. Menyaringnya di sini dan bukan menunggu 400 dari sana:
     batas yang sudah diketahui layar tidak diserahkan kepada galat.

     Yang dipakai adalah ruang yang sedang tampil, jadi aturannya bisa
     dikatakan dalam satu kalimat — yang terbit adalah yang terlihat — dan
     bentuk di ruang satunya tetap tergambar, tetap terdaftar, dan tinggal
     menunggu sumbunya dipindahkan. */
  const publishable = useMemo(
    () => mine.filter((polygon) => polygon.log_x === logX && polygon.log_y === logY),
    [mine, logX, logY],
  );
  const otherSpace = mine.length - publishable.length;

  const [name, setName] = useState("");
  const [classValue, setClassValue] = useState<number | null>(null);
  const [color, setColor] = useState("");
  const [saving, setSaving] = useState(false);

  const [mnemonic, setMnemonic] = useState(DEFAULT_MNEMONIC);
  const [curveSet, setCurveSet] = useState(DEFAULT_SET);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState<CrossplotPublish | null>(null);

  const [doomed, setDoomed] = useState<CrossplotPolygon | null>(null);
  const [deleting, setDeleting] = useState(false);

  const nextClass = classValue ?? freeClass(polygons);
  const nextColor = color || freeColor(polygons);
  const nextName = name.trim() || `Class ${nextClass}`;
  const drawable = drawn.length >= 3 && axes !== null;

  const save = async () => {
    if (!axes) return;
    setSaving(true);
    try {
      await api.post<CrossplotPolygon>("/api/crossplot-polygons/", {
        project: project.id,
        name: nextName,
        color: nextColor,
        x_curve: xCurve.trim().toUpperCase(),
        y_curve: yCurve.trim().toUpperCase(),
        log_x: logX,
        log_y: logY,
        // Di sinilah pecahan kotak gambar berhenti dan nilai kurva mulai.
        vertices: drawn.map((vertex) => vertexToValue(vertex, axes.x, axes.y)),
        class_value: nextClass,
      });
      setName("");
      setClassValue(null);
      setColor("");
      reload();
      onSaved();
      toast(`${nextName} saved as class ${nextClass} on ${xCurve}–${yCurve}.`);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setSaving(false);
    }
  };

  const editing = editingId === null ? null : polygons.find((p) => p.id === editingId) ?? null;

  const saveEdit = async () => {
    if (!axes || !editing || drawn.length < 3) return;
    setSaving(true);
    try {
      await api.patch<CrossplotPolygon>(`/api/crossplot-polygons/${editing.id}/`, {
        vertices: drawn.map((vertex) => vertexToValue(vertex, axes.x, axes.y)),
      });
      reload();
      onEditDone?.();
      toast(`${editing.name} diperbarui — ${count(drawn.length, "corner")}.`);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!doomed) return;
    setDeleting(true);
    try {
      await api.delete(`/api/crossplot-polygons/${doomed.id}/`);
      reload();
      toast(`${doomed.name} deleted.`);
      setDoomed(null);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setDeleting(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const result = await api.post<CrossplotPublish>(
        "/api/crossplot-polygons/publish/",
        {
          project: project.id,
          polygons: publishable.map((polygon) => polygon.id),
          wells: wellIds.length ? wellIds : undefined,
          mnemonic: mnemonic.trim().toUpperCase() || DEFAULT_MNEMONIC,
          set_name: curveSet.trim().toUpperCase() || DEFAULT_SET,
        },
      );
      setPublished(result);
      toast(
        `${result.mnemonic} written to ${result.wells_written} of ${result.wells_asked} wells.`,
      );
      onPublished?.();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Panel title="Polygon classes">
      {editing ? (
        <>
          <span style={hint}>
            Menyunting <b>{editing.name}</b> (kelas {editing.class_value}) langsung di plot. Kurva
            yang sudah terbit tidak berubah sampai kelasnya diterbitkan ulang.
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <Button variant="primary" busy={saving} disabled={!drawable} onClick={saveEdit} full>
              Simpan perubahan
            </Button>
            <Button variant="ghost" onClick={() => onEditDone?.()}>
              Batal
            </Button>
          </div>
        </>
      ) : drawable ? (
        <>
          <span style={hint}>
            Give the shape a name and a class code, and it keeps its place in curve units —
            so retyping an axis bound moves it on the screen without moving the rock it holds.
          </span>
          <TextField label="NAME" value={name} onChange={setName} placeholder={`Class ${nextClass}`} />
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <NumberField
                label="CLASS"
                value={nextClass}
                onChange={(value) => setClassValue(Math.max(0, Math.round(value)))}
                step={1}
                min={0}
              />
            </div>
            <ColorPicker label="Colour" value={nextColor} onChange={setColor} />
          </div>
          <Button variant="primary" busy={saving} onClick={save} full>
            Save shape as a class
          </Button>
        </>
      ) : (
        <span style={hint}>
          {drawn.length
            ? "Three corners are enough to make an area worth saving."
            : "Draw a polygon above, then save it here as a named class."}
        </span>
      )}

      {/* Sakelarnya bergantung pada ada-tidaknya poligon di **lapangan**, dan
          bukan di sepasang sumbu ini. Yang mematikannya lalu berpindah ke
          sumbu yang belum berpoligon dulu kehilangan sakelarnya sendiri,
          sementara pilihannya tetap tersimpan mati — dan pasangan sumbu
          berikutnya yang memang punya bentuk tidak menggambar apa-apa, tanpa
          satu pun kendali yang terlihat untuk menyalakannya kembali. */}
      {polygons.length > 0 && (
        <Toggle label="Show saved polygons" checked={show} onChange={onShowChange} />
      )}

      {mine.length > 0 && (
        <>
          {/* Dua baris per poligon, dan bukan satu.
              Rel ini selebar tiga ratus piksel, dan satu baris yang memuat
              nama, kode, cacah sudut dan tombol hapus sekaligus memberi nama
              itu sisa ruang yang tinggal — yaitu enam huruf. Yang terpotong
              lalu justru satu-satunya yang membedakan sebuah bentuk dari
              bentuk lainnya: "Clean s…" dan "Clean sh…" adalah dua baris yang
              tidak bisa dibedakan, dan keduanya menerbitkan kode yang lain. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {mine.map((polygon) => (
              <div key={polygon.id} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                  <span
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: 2,
                      background: polygon.color,
                      flex: "none",
                    }}
                  />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      font: "400 10.5px var(--font-sans)",
                      color: "var(--ink-3)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={polygon.name}
                  >
                    {polygon.name}
                  </span>
                  {onEdit && (
                    <Button
                      variant={polygon.id === editingId ? "primary" : "ghost"}
                      onClick={() => onEdit(polygon)}
                      disabled={!axes || polygon.log_x !== logX || polygon.log_y !== logY}
                      title={
                        polygon.log_x !== logX || polygon.log_y !== logY
                          ? "Digambar di ruang sumbu yang lain — samakan skala log-nya untuk menyunting"
                          : `Sunting simpul ${polygon.name} di plot`
                      }
                      style={{ height: 20, font: "500 9.5px var(--font-sans)", flex: "none" }}
                    >
                      Sunting
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => setDoomed(polygon)}
                    title={`Delete ${polygon.name}`}
                    style={{ height: 20, font: "500 9.5px var(--font-sans)", flex: "none" }}
                  >
                    Delete
                  </Button>
                </div>
                <span
                  style={{
                    paddingLeft: 16,
                    font: "400 9.5px var(--font-mono)",
                    color: "var(--ink-6)",
                  }}
                >
                  class {polygon.class_value} · {count(polygon.vertices.length, "corner")}
                  {/* Ruang tempat ia digambar ikut disebut. Sebuah poligon
                      yang lahir di atas sumbu logaritmik menggolongkan di ruang
                      itu selamanya, dan barisnya yang diam soal itu membuat
                      bentuk yang tampak melengkung terbaca sebagai kekeliruan
                      gambar. */}
                  {polygon.log_x || polygon.log_y
                    ? ` · log ${[polygon.log_x && "x", polygon.log_y && "y"].filter(Boolean).join("")}`
                    : ""}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {mine.length === 0 && polygons.length > 0 && (
        <Empty>No polygon stands on {xCurve}–{yCurve} yet.</Empty>
      )}
      {elsewhere > 0 && (
        <span style={hint}>
          {count(elsewhere, "more polygon", "more polygons")}{" "}
          {elsewhere === 1 ? "stands" : "stand"} on other pairs of axes. A shape only means
          anything on the axes it was drawn over.
        </span>
      )}

      {mine.length > 0 && (
        <>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <TextField label="CURVE" value={mnemonic} onChange={setMnemonic} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <TextField label="SET" value={curveSet} onChange={setCurveSet} />
            </div>
          </div>
          <Button
            variant="primary"
            busy={publishing}
            disabled={!mayPublish || publishable.length === 0}
            onClick={publish}
            title={
              !mayPublish
                ? "Your role on this project may not publish curves"
                : publishable.length === 0
                  ? "No shape here was drawn in the axis space now shown"
                  : "Classify every sample and write the codes as a curve"
            }
            full
          >
            Publish {count(publishable.length, "polygon")} to{" "}
            {wellIds.length ? count(wellIds.length, "well") : "every well"}
          </Button>
          {otherSpace > 0 && (
            <span style={hint}>
              {otherSpace} of these {otherSpace === 1 ? "was" : "were"} drawn in the other axis
              space — {logX || logY ? "linear" : "logarithmic"} where this screen is now{" "}
              {logX || logY ? "logarithmic" : "linear"} — and {otherSpace === 1 ? "it stays" : "they stay"}{" "}
              out of this curve. One class curve can only be read in one space. Switch the axis to
              publish {otherSpace === 1 ? "it" : "them"} instead.
            </span>
          )}
          <span style={hint}>
            Each well is classified over its whole depth, not over the interval picked above: a
            class curve that is filled in one zone only reads as a well with no rock outside it.
          </span>
        </>
      )}

      {/* Laporan penerbitan hanya berdiri di atas sumbu yang melahirkannya.
          Tanpa penjagaan ini, berpindah ke GR–RT memajang kalimat "No polygon
          stands on GR–RT yet" tepat di atas laporan lengkap yang menyebut
          cacah NPHI–RHOB. Disembunyikan dan bukan dibuang: angkanya tetap
          benar, dan ia muncul kembali utuh ketika sumbunya kembali. */}
      {published
        && published.x_curve === xCurve.trim().toUpperCase()
        && published.y_curve === yCurve.trim().toUpperCase()
        && published.log_x === logX
        && published.log_y === logY && <PublishedNote result={published} />}

      {doomed && (
        <DeleteDialog
          title="Delete polygon"
          what={`${doomed.name} (class ${doomed.class_value})`}
          consequences={[]}
          keeps="Curves already published keep the codes they were written with — only the shape goes."
          busy={deleting}
          onCancel={() => setDoomed(null)}
          onConfirm={remove}
        />
      )}
    </Panel>
  );
}

/**
 * Apa yang benar-benar tertulis, per kelas dan per sumur yang dilewati.
 *
 * Cacah per kelas ikut sebab itulah satu-satunya cara memeriksa sebuah
 * penggolongan tanpa membuka lembar log: sebuah kelas yang memulangkan nol
 * sampel di seluruh lapangan adalah poligon yang digambar di tempat yang tidak
 * ada batuannya, dan itu tidak terlihat dari kurva yang terbit rapi.
 */
function PublishedNote({ result }: { result: CrossplotPublish }) {
  /* Baris dibuat dari poligon yang **diminta**, bukan dari kelas yang
     kebetulan terisi. `classified` datang dari sebuah Counter di server, dan
     sebuah Counter tidak punya kunci untuk kelas yang tidak pernah terhitung —
     jadi poligon yang tidak menangkap satu sampel pun lenyap dari daftar ini
     tanpa satu baris pun yang kurang kelihatan. Itu persis keadaan yang
     membuat laporan ini ada: tiga poligon terbit, satu kosong, dan yang
     membacanya melihat dua baris yang rapi. */
  const rows = [...result.polygons]
    .sort((a, b) => a.class_value - b.class_value)
    .map((polygon) => ({
      polygon,
      n: result.classified[String(polygon.class_value)] ?? 0,
    }));
  const caught = rows.reduce((sum, row) => sum + row.n, 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <KeyValue k="Written" v={`${result.wells_written} of ${count(result.wells_asked, "well")}`} />
      <KeyValue k="Curve" v={`${result.set_name} · ${result.mnemonic}`} />
      {caught === 0 && (
        <Notice tone="warn">
          No sample of any well falls inside these polygons. The shapes hold no rock where they
          stand.
        </Notice>
      )}
      {rows.map(({ polygon, n }) => (
        <KeyValue
          key={polygon.id}
          k={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span
                style={{ width: 8, height: 8, borderRadius: 2, background: polygon.color }}
              />
              {polygon.name}
            </span>
          }
          v={
            n === 0
              // Bukan "0 samples" yang duduk rapi di antara angka-angka
              // lainnya. Sebuah poligon yang tidak menangkap apa pun adalah
              // bentuk yang digambar di tempat yang tidak ada batuannya, dan
              // itu keterangan tentang poligonnya — bukan hasil pengukuran.
              ? "caught nothing"
              : `${n.toLocaleString("en-US")} samples`
          }
        />
      ))}
      {result.skipped.length > 0 && (
        <span style={hint}>
          Skipped: {result.skipped.map((row) => `${row.well} — ${row.reason}`).join("; ")}.
        </span>
      )}
    </div>
  );
}

const hint = {
  font: "400 9.5px var(--font-sans)",
  color: "var(--ink-6)",
  lineHeight: 1.6,
} as const;
